<template>
  <div class="history-page">
    <h2>Route History (VRP, TSP & LAP)</h2>
    <div v-if="isLoading" class="loading-message">Loading routes...</div>
    <div v-else-if="routes.length === 0" class="empty-message">No routes found.</div>
    <div v-else class="history-list">
      <div
        v-for="(route, idx) in routes"
        :key="route._id || idx"
        class="history-item"
        :class="{ expanded: expandedIndex === idx }"
      >
        <!-- Header (always visible) -->
        <div class="history-header" @click="toggleExpand(idx)">
          <div class="status-logo" :class="statusClass(route.status)">
            <svg width="48" height="48" viewBox="0 0 48 48">
              <defs>
                <linearGradient :id="'grad-' + idx" x1="0" y1="0" x2="1" y2="1">
                  <stop :offset="statusGradient(route.status).start.offset" :stop-color="statusGradient(route.status).start.color"/>
                  <stop :offset="statusGradient(route.status).end.offset" :stop-color="statusGradient(route.status).end.color"/>
                </linearGradient>
              </defs>
              <circle cx="24" cy="24" r="20" :fill="'url(#grad-' + idx + ')'" />
            </svg>
          </div>          <div class="route-info">
            <div class="route-name">{{ route.name || 'Unnamed Route' }}</div>
            <div class="route-date">{{ formatDate(route.timestamp) }}</div>
            <div class="route-duration" v-if="route.total_duration_formatted || route.total_duration">
              Duration: {{ route.total_duration_formatted || formatDuration(route.total_duration) }}
            </div>
          </div>          <div class="route-metrics">
            <div class="route-distance">
              <span class="distance-value">{{ (route.total_distance / 1000).toFixed(1) }}</span>
              <span class="distance-unit">km</span>
            </div>
            <div class="carbon-footprint">
              <img src="@/assets/footprint.png" alt="Carbon Footprint" class="footprint-icon" />
              <span class="carbon-value">{{ calculateCarbonFootprint(route).formatted }}</span>
            </div>
          </div>
          <div class="expand-arrow" :class="{ open: expandedIndex === idx }">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <polyline points="6 9 12 15 18 9" fill="none" stroke="#888" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>        <!-- Sliding Details -->
        <transition name="slide-fade">
          <div v-if="expandedIndex === idx" class="route-details">            <div class="detail-row">
              <span class="detail-label">Total Duration:</span>
              <span>{{ route.total_duration_formatted || formatDuration(route.total_duration) || 'N/A' }}</span>
            </div>            <div class="detail-row">
              <span class="detail-label">Total Distance:</span>
              <span>{{ (route.total_distance / 1000).toFixed(2) }} km</span>
            </div>            <div class="detail-row">
              <span class="detail-label">Carbon Footprint:</span>
              <span>{{ calculateCarbonFootprint(route).formatted }} <span class="carbon-details">({{ calculateCarbonFootprint(route).formattedPerKm }}/km)</span></span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Average Speed:</span>
              <span>30 km/h (estimated)</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Solver Type:</span>
              <span class="solver-type" :class="getSolverTypeClass(route)">{{ getSolverTypeDisplay(route) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Depot:</span>
              <span>{{ route.locationNames?.[0] || 'N/A' }} <span class="coords">({{ formatCoords(route.allLocations?.[0]) }})</span></span>
            </div>
            
            <!-- LAP-specific Details -->
            <div v-if="getSolverType(route) === 'LAP'" class="lap-section">
              <h4 class="section-title">Allocation Details</h4>
              
              <!-- LAP Metrics -->
              <div class="lap-metrics">
                <div class="metric-row">
                  <span class="metric-label">Total Cost:</span>
                  <span class="metric-value">{{ route.total_cost?.toFixed(2) || 'N/A' }}</span>
                </div>
                <div class="metric-row">
                  <span class="metric-label">Avg Distance:</span>
                  <span class="metric-value">{{ route.average_distance ? (route.average_distance / 1000).toFixed(2) + ' km' : 'N/A' }}</span>
                </div>
                <div class="metric-row">
                  <span class="metric-label">Assignments:</span>
                  <span class="metric-value">{{ route.num_assignments || route.assignments?.length || 'N/A' }}</span>
                </div>
              </div>
              
              <!-- Facility Utilization -->
              <div v-if="route.facility_utilization || route.facility_loads" class="facility-utilization">
                <h5>Facility Utilization</h5>
                <div v-for="(utilization, facilityIdx) in route.facility_utilization || {}" :key="'facility-' + facilityIdx" class="utilization-row">
                  <div class="facility-info">
                    <span class="facility-name">{{ getFacilityName(route, facilityIdx) }}</span>
                    <span class="utilization-percentage">{{ (utilization.utilization_rate * 100).toFixed(1) }}%</span>
                  </div>
                  <div class="utilization-bar">
                    <div class="utilization-fill" :style="{ width: (utilization.utilization_rate * 100) + '%' }"></div>
                  </div>
                  <div class="utilization-details">
                    {{ utilization.load || 0 }}/{{ utilization.capacity || 0 }} units
                  </div>
                </div>
              </div>
            </div>

            <!-- Vehicle Details -->
            <div v-if="route.routes && route.routes.length > 0 && getSolverType(route) !== 'LAP'" class="vehicles-section">
              <h4 class="section-title">{{ getSolverType(route) === 'TSP' ? 'Route Details' : 'Vehicle Routes' }}</h4>
              <div v-for="(vehicleRoute, vIdx) in route.routes" :key="vIdx" class="vehicle-details">
                <div class="vehicle-header" @click="toggleVehicleExpand(idx, vIdx)">
                  <div class="vehicle-title-section">
                    <span class="vehicle-title">{{ getVehicleDisplayName(vehicleRoute, route) }}</span>
                    <span class="vehicle-id" v-if="vehicleRoute.vehicleId">({{ getVehicleTypeAndCapacity(vehicleRoute.vehicleId) }})</span>
                  </div>
                  <div class="vehicle-stats">
                    <span class="stat">{{ (vehicleRoute.distance / 1000).toFixed(1) }}km</span>
                    <span class="stat">{{ vehicleRoute.duration_formatted || formatDuration(vehicleRoute.duration_minutes) }}</span>
                    <span class="stat" v-if="vehicleRoute.average_speed_kmh">{{ vehicleRoute.average_speed_kmh }}km/h avg</span>
                    <span class="stat delivery-stat" v-if="getTotalDeliveries(vehicleRoute, route) && getSolverType(route) === 'VRP'">
                      📦 {{ getTotalDeliveries(vehicleRoute, route) }} units
                    </span>
                    <span class="stat location-stat" v-if="getSolverType(route) === 'TSP' && vehicleRoute.reach_times">
                      📍 {{ vehicleRoute.reach_times.length - 1 }} locations
                    </span>
                  </div>
                  <div class="vehicle-expand-arrow" :class="{ open: isVehicleExpanded(idx, vIdx) }">
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <polyline points="6 9 12 15 18 9" fill="none" stroke="#888" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
                  <!-- Reach Times for this vehicle -->
                <transition name="vehicle-slide-fade">
                  <div v-if="isVehicleExpanded(idx, vIdx) && vehicleRoute.reach_times && vehicleRoute.reach_times.length > 0" class="reach-times">
                  <h5 class="reach-times-title">Stop Schedule</h5>
                  <div class="timeline">
                    <div 
                      v-for="(reachTime, rtIdx) in vehicleRoute.reach_times" 
                      :key="rtIdx" 
                      class="timeline-item"
                      :class="{ 
                        'is-depot': isDepotLocation(reachTime.location_index, route),
                        'on-time': isDeliveryOnTime(reachTime, route),
                        'late': isDeliveryLate(reachTime, route)
                      }"
                    >
                      <div class="timeline-marker"></div>
                      <div class="timeline-content">
                        <div class="location-name">
                          {{ getLocationName(reachTime.location_index, route) }}
                        </div>                        <div class="reach-time">
                          {{ reachTime.actual_arrival_time || formatRealTime(reachTime.reach_time_minutes, route) }}
                        </div>
                        
                        <!-- Show demand quantity for VRP or location visit for TSP -->
                        <div class="demand-quantity" :class="{ 'is-depot': isDepotLocation(reachTime.location_index, route) }" v-if="getSolverType(route) === 'VRP' || getLocationDemand(reachTime.location_index, route) > 0">
                          <span class="demand-label">
                            {{ isDepotLocation(reachTime.location_index, route) ? 'Pickup/Return:' : 'Delivery:' }}
                          </span>
                          <span class="demand-value" :class="{ 
                            'depot-value': isDepotLocation(reachTime.location_index, route),
                            'delivery-value': !isDepotLocation(reachTime.location_index, route)
                          }">
                            {{ getLocationDemand(reachTime.location_index, route) || 0 }} units
                          </span>
                          <span v-if="!isDepotLocation(reachTime.location_index, route)" class="delivery-icon">📦</span>
                          <span v-else class="depot-icon">🏢</span>
                        </div>
                        <!-- For TSP routes without demands, show visit indicator -->
                        <div class="visit-indicator" v-if="getSolverType(route) === 'TSP' && !isDepotLocation(reachTime.location_index, route) && getLocationDemand(reachTime.location_index, route) === 0">
                          <span class="visit-label">Visit:</span>
                          <span class="visit-value">Stop {{ reachTime.location_index }}</span>
                          <span class="visit-icon">📍</span>
                        </div>
                        <!-- Show time window if available -->
                        <div v-if="getLocationTimeWindow(reachTime.location_index, route)" class="time-window">
                          <span class="time-window-label">Time Window:</span>
                          <span class="time-window-value">{{ getLocationTimeWindow(reachTime.location_index, route) }}</span>
                          <span v-if="isDeliveryLate(reachTime, route)" class="late-indicator">⚠️ Late</span>
                          <span v-else-if="isDeliveryOnTime(reachTime, route)" class="on-time-indicator">✅ On Time</span>
                        </div>
                        <div class="location-coords" v-if="getLocationCoords(reachTime.location_index, route)">
                          {{ formatCoords(getLocationCoords(reachTime.location_index, route)) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </transition>
              </div>
            </div>
              <!-- Legacy vehicle display if no detailed routes -->
            <div v-else class="detail-row">
              <span class="detail-label">Used Vehicles:</span>
              <span v-if="route.vehicleIds && route.vehicleIds.length">{{ getVehiclePlatesText(route.vehicleIds) }}</span>
              <span v-else>N/A</span>
            </div>
            
            <!-- Status Actions -->
            <div class="status-actions">
              <button
                class="status-btn done"
                :class="{ active: route.status === 'done' }"
                @click.stop="updateStatus(route, 'done')"
              >Done</button>
              <button
                class="status-btn planned"
                :class="{ active: route.status === 'planned' || !route.status }"
                @click.stop="updateStatus(route, 'planned')"
              >Planned</button>
              <button
                class="status-btn rejected"
                :class="{ active: route.status === 'rejected' }"
                @click.stop="updateStatus(route, 'rejected')"
              >Rejected</button>
            </div>
            
            <!-- Download Section -->
            <div class="download-section">
              <button
                class="download-btn pdf-btn"
                :class="{ loading: downloadingPDF }"
                @click.stop="downloadPDF(route)"
                :disabled="downloadingPDF || downloadingExcel || deletingRoute"
              >
                <svg v-if="!downloadingPDF" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span v-if="downloadingPDF" class="loading-spinner"></span>
                {{ downloadingPDF ? 'Rapor Hazırlanıyor...' : 'PDF İndir' }}
              </button>
              
              <button
                class="download-btn excel-btn"
                :class="{ loading: downloadingExcel }"
                @click.stop="downloadExcel(route)"
                :disabled="downloadingExcel || downloadingPDF || deletingRoute"
              >
                <svg v-if="!downloadingExcel" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H5a2 2 0 0 0-2-2z"/>
                  <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4"/>
                  <path d="M9 7v6"/>
                  <path d="M15 7v6"/>
                </svg>
                <span v-if="downloadingExcel" class="loading-spinner"></span>
                {{ downloadingExcel ? 'Excel Hazırlanıyor...' : 'Excel İndir' }}
              </button>
              
              <button
                class="download-btn map-btn"
                @click.stop="openMapModal(route)"
                :disabled="downloadingPDF || downloadingExcel || deletingRoute"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                  <line x1="8" y1="2" x2="8" y2="18"/>
                  <line x1="16" y1="6" x2="16" y2="22"/>
                </svg>
                Haritada Görüntüle
              </button>
              
              <button
                class="download-btn edit-btn"
                @click.stop="openEditModal(route)"
                :disabled="downloadingPDF || downloadingExcel || deletingRoute || !canEditRoute(route)"
                :title="getEditTooltip(route)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Rotayı Düzenle
              </button>
              
              <button
                class="download-btn delete-btn"
                :class="{ loading: deletingRoute }"
                @click.stop="deleteRoute(route)"
                :disabled="deletingRoute || downloadingPDF || downloadingExcel"
              >
                <svg v-if="!deletingRoute" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3,6 5,6 21,6"/>
                  <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
                <span v-if="deletingRoute" class="loading-spinner"></span>
                {{ deletingRoute ? 'Siliniyor...' : 'Sil' }}
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>
    
    <!-- Route Map Modal -->
    <RouteMapModal 
      v-if="showMapModal"
      :route="selectedRouteForMap"
      :vehicles="vehicles"
      @close="closeMapModal"
    />
    
    <!-- Route Edit Modal -->
    <RouteEditModal 
      v-if="showEditModal"
      :route="selectedRouteForEdit"
      :vehicles="vehicles"
      @close="closeEditModal"
      @updated="onRouteUpdated"
    />
  </div>
</template>

<script>
import axios from 'axios'
import VehiclesService from '@/services/VehiclesService'
import LocationsService from '@/services/LocationsService'
import CarbonFootprintCalculator from '@/utils/CarbonFootprintCalculator'
import PDFTemplates from '@/utils/PDFTemplates'
import ExcelExportService from '@/services/ExcelExportService'
import RouteMapModal from '@/components/RouteMapModal.vue'
import RouteEditModal from '@/components/RouteEditModal.vue'
const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:5000';

export default {
  name: 'History',
  components: {
    RouteMapModal,
    RouteEditModal
  },
  data() {
    return {
      routes: [],
      vehicles: [], // Add vehicles array to cache vehicle data
      locations: [], // Add locations cache for demand lookup
      isLoading: true,
      expandedIndex: null,
      expandedVehicles: {}, // Track which vehicle sections are expanded for each route
      downloadingPDF: false,
      downloadingExcel: false,
      deletingRoute: false,
      showMapModal: false,
      selectedRouteForMap: null,
      showEditModal: false,
      selectedRouteForEdit: null,
    }
  },  methods: {    async fetchRoutes() {
      this.isLoading = true;
      try {
        // Fetch routes, vehicles, and locations in parallel
        const [routesResponse, vehiclesResponse, locationsResponse] = await Promise.all([
          axios.get(`${API_URL}/api/analytics/saved-routes`),
          VehiclesService.getVehicles(),
          LocationsService.getLocations()
        ]);
        
        // Store vehicles and locations for lookup
        this.vehicles = vehiclesResponse.data || [];
        this.locations = locationsResponse.data || [];
          // Normalize route IDs 
        this.routes = routesResponse.data.slice().reverse().map(r => {
          // Prefer _id (MongoDB ObjectId) over custom id field
          const id = r._id || r.id;
          
          // Debug: Log route data to see what we're getting
          console.log('Route data:', {
            name: r.name,
            hasDemands: 'demands' in r,
            hasTimeWindows: 'time_windows' in r,
            hasLocationData: 'locationData' in r,
            demands: r.demands,
            locationData: r.locationData,
            allLocations: r.allLocations,
            locationNames: r.locationNames,
            routes: r.routes && r.routes.length ? r.routes.map(route => ({
              vehicle: route.vehicle,
              vehicleId: route.vehicleId,
              reach_times: route.reach_times ? route.reach_times.length : 0,
              reach_times_sample: route.reach_times ? route.reach_times.slice(0, 2) : null
            })) : null
          });
          
          return { ...r, id };
        });
        
        console.log('Total routes loaded:', this.routes.length);
      } catch (e) {
        console.error('Failed to load routes:', e);
        this.routes = [];
        this.vehicles = [];
      } finally {
        this.isLoading = false;
      }
    },
    statusClass(status) {
      switch ((status || '').toLowerCase()) {
        case 'done': return 'done';
        case 'rejected': return 'rejected';
        case 'planned': return 'planned';
        case 'success': return 'done';
        default: return 'planned';
      }
    },
    statusGradient(status) {
      switch ((status || '').toLowerCase()) {
        case 'done':
        case 'success':
          return { start: { offset: '0%', color: '#43e97b' }, end: { offset: '100%', color: '#38f9d7' } };
        case 'rejected':
          return { start: { offset: '0%', color: '#fa709a' }, end: { offset: '100%', color: '#fee140' } };
        case 'planned':
        default:
          return { start: { offset: '0%', color: '#f7971e' }, end: { offset: '100%', color: '#ffd200' } };
      }
    },
    formatDate(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      return d.toLocaleString();
    },
    formatCoords(coords) {
      if (!coords || coords.length !== 2) return '';
      return `${coords[1].toFixed(5)}, ${coords[0].toFixed(5)}`;
    },
    toggleExpand(idx) {
      this.expandedIndex = this.expandedIndex === idx ? null : idx;
    },    visitedLocations(route) {
      if (!route.locationNames || route.locationNames.length < 2) return [];
      return route.locationNames.slice(1);
    },
    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return 'N/A';
      const hours = Math.floor(minutes / 60);
      const mins = Math.floor(minutes % 60);
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    },
    isDepotLocation(locationIndex, route) {
      // Check if this location is a depot
      if (route.multiDepot && route.depots) {
        return route.depots.includes(locationIndex);
      }
      return locationIndex === 0; // Single depot case
    },
    getLocationName(locationIndex, route) {
      if (route.locationNames && route.locationNames[locationIndex]) {
        return route.locationNames[locationIndex];
      }
      if (this.isDepotLocation(locationIndex, route)) {
        return 'Depot';
      }
      return `Location ${locationIndex}`;
    },    getLocationCoords(locationIndex, route) {
      if (route.allLocations && route.allLocations[locationIndex]) {
        return route.allLocations[locationIndex];
      }
      return null;
    },    getVehiclePlate(vehicleId) {
      if (!vehicleId || !this.vehicles.length) {
        return vehicleId || 'Unknown';
      }
      
      const vehicle = this.vehicles.find(v => v.id === vehicleId);
      return vehicle ? vehicle.plate : vehicleId;
    },    getVehiclePlatesText(vehicleIds) {
      if (!vehicleIds || !vehicleIds.length) {
        return 'N/A';
      }
      
      return vehicleIds.map(id => this.getVehiclePlate(id)).join(', ');
    },
    getVehicleDisplayName(vehicleRoute, route) {
      // For TSP routes, use "Route" instead of "Vehicle" if single route
      if (this.getSolverType(route) === 'TSP' && route.routes && route.routes.length === 1) {
        return 'Optimal Route';
      }
      
      if (!vehicleRoute.vehicleId || !this.vehicles.length) {
        return `Vehicle ${vehicleRoute.vehicle + 1}`;
      }
      
      const vehicle = this.vehicles.find(v => v.id === vehicleRoute.vehicleId);
      return vehicle ? vehicle.plate : `Vehicle ${vehicleRoute.vehicle + 1}`;
    },    getVehicleTypeAndCapacity(vehicleId) {
      if (!vehicleId || !this.vehicles.length) {
        return '';
      }
      
      const vehicle = this.vehicles.find(v => v.id === vehicleId);
      if (!vehicle) return '';
      
      const type = vehicle.type || 'Unknown';
      const capacity = vehicle.capacity || 'N/A';
      return `${type}, ${capacity} units`;
    },    /**
     * Get the demand quantity for a specific location
     */
    getLocationDemand(locationIndex, route) {
      // For depot locations, demand is always 0
      if (this.isDepotLocation(locationIndex, route)) {
        return 0;
      }
      
      // First priority: Check if this is from a route with reach_times that contain demand info (most reliable)
      if (route.routes && Array.isArray(route.routes)) {
        for (const vehicleRoute of route.routes) {
          if (vehicleRoute.reach_times && Array.isArray(vehicleRoute.reach_times)) {
            const reachTime = vehicleRoute.reach_times.find(rt => rt.location_index === locationIndex);
            if (reachTime && reachTime.demand !== undefined && reachTime.demand !== null) {
              console.log(`📦 Found demand from reach_times for location ${locationIndex}:`, reachTime.demand);
              return reachTime.demand;
            }
          }
        }
      }
      
      // Second priority: Check if route has detailed location data with demands
      if (route.locationData && Array.isArray(route.locationData) && route.locationData[locationIndex]) {
        const locationData = route.locationData[locationIndex];
        const demand = locationData.demand;
        if (demand !== undefined && demand !== null) {
          console.log(`📦 Found demand from locationData for location ${locationIndex}:`, demand, 'for location:', locationData.name);
          return demand;
        }
      }
      
      // Third priority: Check if route has demands array
      if (route.demands && Array.isArray(route.demands)) {
        if (route.demands[locationIndex] !== undefined && route.demands[locationIndex] !== null) {
          const demand = route.demands[locationIndex];
          console.log(`📦 Found demand from demands array for location ${locationIndex}:`, demand);
          return demand;
        }
      }
      
      // Fourth priority: Try to extract from location names if they contain demand info
      if (route.locationNames && route.locationNames[locationIndex]) {
        const locationName = route.locationNames[locationIndex];
        const demandMatch = locationName.match(/\((?:Demand|demand):\s*(\d+)\)/);
        if (demandMatch) {
          const demand = parseInt(demandMatch[1], 10);
          console.log(`📦 Found demand from location name for location ${locationIndex}:`, demand);
          return demand;
        }
      }
      
      // Fifth priority: Lookup demand from locations database as a last resort
      if (this.locations.length) {
        const location = this.locations.find(loc => loc.index === locationIndex);
        if (location && location.demand !== undefined && location.demand !== null) {
          console.log(`📦 Found demand from locations database for location ${locationIndex}:`, location.demand);
          return location.demand;
        }
      }
      
      // Default to 1 for non-depot locations if no demand data is available
      console.log(`⚠️ No demand data found for location ${locationIndex}, defaulting to 1`);
      return 1;
    },
    
    /**
     * Get the time window for a specific location
     */
    getLocationTimeWindow(locationIndex, route) {
      // Skip time windows for depot locations
      if (this.isDepotLocation(locationIndex, route)) {
        return null;
      }
      
      // Check if route has time windows array
      if (route.time_windows && route.time_windows[locationIndex]) {
        const timeWindow = route.time_windows[locationIndex];
        
        // Handle different time window formats
        if (typeof timeWindow === 'object' && timeWindow.timeWindowStart !== undefined) {
          // MongoDB format: {timeWindowStart: seconds, timeWindowEnd: seconds}
          return `${this.formatSecondsToTime(timeWindow.timeWindowStart)} - ${this.formatSecondsToTime(timeWindow.timeWindowEnd)}`;
        } else if (Array.isArray(timeWindow) && timeWindow.length >= 2) {
          // Array format: [start_seconds, end_seconds]
          return `${this.formatSecondsToTime(timeWindow[0])} - ${this.formatSecondsToTime(timeWindow[1])}`;
        }
      }
      
      // Check if route has detailed location data with time windows
      if (route.locationData && route.locationData[locationIndex]) {
        const location = route.locationData[locationIndex];
        if (location.timeWindowStart != null && location.timeWindowEnd != null) {
          return `${this.formatSecondsToTime(location.timeWindowStart)} - ${this.formatSecondsToTime(location.timeWindowEnd)}`;
        }
      }
      
      return null;
    },
    
    /**
     * Check if delivery was made on time
     */
    isDeliveryOnTime(reachTime, route) {
      if (this.isDepotLocation(reachTime.location_index, route)) {
        return true; // Depot visits are always considered "on time"
      }
      
      const timeWindow = this.getLocationTimeWindow(reachTime.location_index, route);
      if (!timeWindow) return null; // No time window to check against
      
      // Convert reach time from minutes to seconds for comparison
      const deliveryTimeSeconds = this.convertReachTimeToSeconds(reachTime.reach_time_minutes, route);
      
      const windowParts = timeWindow.split(' - ');
      if (windowParts.length !== 2) return null;
      
      const windowStartSeconds = this.parseTimeToSeconds(windowParts[0]);
      const windowEndSeconds = this.parseTimeToSeconds(windowParts[1]);
      
      return deliveryTimeSeconds >= windowStartSeconds && deliveryTimeSeconds <= windowEndSeconds;
    },
    
    /**
     * Check if delivery was late
     */
    isDeliveryLate(reachTime, route) {
      if (this.isDepotLocation(reachTime.location_index, route)) {
        return false; // Depot visits are never late
      }
      
      const onTime = this.isDeliveryOnTime(reachTime, route);
      return onTime === false; // Only late if we can determine it's not on time
    },
    
    /**
     * Format minutes to real time (assuming start time is 8:00 AM)
     */
    formatRealTime(minutes, route) {
      if (!minutes && minutes !== 0) return 'N/A';
      
      // Default start time is 8:00 AM (can be made configurable later)
      const startHour = route.startTime ? parseInt(route.startTime.split(':')[0]) : 8;
      const startMinute = route.startTime ? parseInt(route.startTime.split(':')[1]) : 0;
      
      // Calculate total minutes from midnight
      const totalStartMinutes = startHour * 60 + startMinute;
      const totalMinutes = totalStartMinutes + minutes;
      
      // Convert back to hours and minutes
      const hours = Math.floor(totalMinutes / 60) % 24;
      const mins = Math.floor(totalMinutes % 60);
      
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    },
    
    /**
     * Format seconds from start of day to HH:MM format
     */
    formatSecondsToTime(seconds) {
      if (!seconds && seconds !== 0) return 'N/A';
      
      // Convert seconds to hours and minutes
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    },
    
    /**
     * Parse time string to minutes from midnight
     */
    parseRealTime(timeString) {
      if (!timeString || timeString === 'N/A') return 0;
      
      const parts = timeString.split(':');
      if (parts.length !== 2) return 0;
      
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      
      return hours * 60 + minutes;
    },
    
    /**
     * Parse time string (HH:MM) to seconds from midnight
     */
    parseTimeToSeconds(timeString) {
      if (!timeString || timeString === 'N/A') return 0;
      
      const parts = timeString.split(':');
      if (parts.length !== 2) return 0;
      
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      
      return hours * 3600 + minutes * 60;
    },
    
    /**
     * Convert reach time from minutes (relative to start) to seconds from midnight
     */
    convertReachTimeToSeconds(reachTimeMinutes, route) {
      if (!reachTimeMinutes && reachTimeMinutes !== 0) return 0;
      
      // Default start time is 8:00 AM (28800 seconds)
      const startHour = route.startTime ? parseInt(route.startTime.split(':')[0]) : 8;
      const startMinute = route.startTime ? parseInt(route.startTime.split(':')[1]) : 0;
      
      // Calculate start time in seconds from midnight
      const startTimeSeconds = startHour * 3600 + startMinute * 60;
      
      // Convert reach time minutes to seconds and add to start time
      return startTimeSeconds + (reachTimeMinutes * 60);
    },
    
    /**
     * Calculate total deliveries for a vehicle route
     */
    getTotalDeliveries(vehicleRoute, route) {
      if (!vehicleRoute.reach_times || !vehicleRoute.reach_times.length) {
        return 0;
      }
      
      let totalDeliveries = 0;
      
      vehicleRoute.reach_times.forEach(reachTime => {
        // Skip depot locations
        if (!this.isDepotLocation(reachTime.location_index, route)) {
          const demand = this.getLocationDemand(reachTime.location_index, route);
          if (demand && demand > 0) {
            totalDeliveries += demand;
          }
        }
      });
        return totalDeliveries;
    },
    
    /**
     * Calculate carbon footprint for a route
     * @param {Object} route - Route object containing distance and vehicle data
     * @returns {Object} Carbon footprint calculation results
     */
    calculateCarbonFootprint(route) {
      if (!route || !route.total_distance) {
        return {
          totalCO2: 0,
          formatted: '0.0 kg CO₂',
          formattedPerKm: '0.0 kg CO₂',
          details: 'No distance data'
        };
      }

      const distanceKm = route.total_distance / 1000;
      
      // Determine vehicle type(s) for the route
      let vehicleData = 'default';
      
      // Check if route has multiple vehicles with different types
      if (route.routes && Array.isArray(route.routes)) {
        vehicleData = route.routes.map(vehicleRoute => {
          // Try to get vehicle details from our cached vehicles data
          if (vehicleRoute.vehicleId && this.vehicles.length > 0) {
            const vehicle = this.vehicles.find(v => v.id === vehicleRoute.vehicleId);
            return {
              type: vehicle?.type || 'default',
              capacity: vehicle?.capacity,
              vehicleId: vehicleRoute.vehicleId
            };
          }
          
          // Fallback to route-level vehicle information
          return {
            type: route.vehicleType || route.vehicle_type || 'default'
          };
        });
      } else if (route.vehicleIds && Array.isArray(route.vehicleIds)) {
        // Handle case where we have vehicle IDs but no detailed route data
        vehicleData = route.vehicleIds.map(vehicleId => {
          const vehicle = this.vehicles.find(v => v.id === vehicleId);
          return {
            type: vehicle?.type || 'default',
            capacity: vehicle?.capacity,
            vehicleId: vehicleId
          };
        });
      } else {
        // Single vehicle or generic vehicle type
        vehicleData = route.vehicleType || route.vehicle_type || 'default';
      }
        return CarbonFootprintCalculator.calculateRouteFootprint(distanceKm, vehicleData);
    },
    
    /**
     * Get carbon footprint comparison data for a route
     * @param {Object} route - Route object
     * @returns {Object} Carbon comparison data
     */
    getCarbonComparison(route) {
      const footprint = this.calculateCarbonFootprint(route);
      return CarbonFootprintCalculator.getCarbonComparison(footprint.totalCO2);
    },
    
    /**
     * Determine the solver type based on route characteristics
     * @param {Object} route - Route object
     * @returns {String} Solver type ('VRP', 'TSP', or 'Unknown')
     */
    getSolverType(route) {
      // Check if route has explicit solver type field
      if (route.solver_type) {
        return route.solver_type.toUpperCase();
      }
      
      // Check for LAP-specific fields
      if (route.facilities && route.demands && route.assignments) {
        return 'LAP';
      }
      
      if (route.facility_utilization || route.facility_loads) {
        return 'LAP';
      }
      
      // Infer solver type based on route characteristics
      // VRP is indicated by multiple vehicles, capacity constraints, or explicit VRP flags.
      if ((route.demands && route.demands.some(d => d > 0)) || 
          route.vehicle_capacity || 
          route.vehicle_capacities) {
        return 'VRP';
      }
      
      if (route.routes && route.routes.length > 1) {
        return 'VRP';
      }
      
      if (route.num_vehicles && route.num_vehicles > 1) {
        return 'VRP';
      }
      
      // If it's a single route without VRP characteristics, it's likely TSP.
      if (route.routes && route.routes.length === 1) {
        return 'TSP';
      }
      
      // Default to TSP for single-route problems as a fallback.
      return 'TSP';
    },
    
    /**
     * Get display text for solver type
     * @param {Object} route - Route object
     * @returns {String} Display text for solver type
     */
    getSolverTypeDisplay(route) {
      const solverType = this.getSolverType(route);
      switch (solverType) {
        case 'VRP':
          return 'Vehicle Routing Problem (VRP)';
        case 'TSP':
          return 'Traveling Salesman Problem (TSP)';
        case 'LAP':
          return 'Location Allocation Problem (LAP)';
        default:
          return 'Unknown';
      }
    },
    
    /**
     * Get CSS class for solver type styling
     * @param {Object} route - Route object
     * @returns {String} CSS class name
     */
    getSolverTypeClass(route) {
      const solverType = this.getSolverType(route);
      return `solver-${solverType.toLowerCase()}`;
    },
    
    /**
     * Get facility name for LAP history display
     * @param {Object} route - Route object
     * @param {Number} facilityIdx - Facility index
     * @returns {String} Facility name
     */
    getFacilityName(route, facilityIdx) {
      if (route.facilities && route.facilities[facilityIdx]) {
        return route.facilities[facilityIdx].name || `Facility ${facilityIdx + 1}`;
      }
      return `Facility ${facilityIdx + 1}`;
    },
    
    /**
     * Download PDF report for the route
     */
    async downloadPDF(route) {
      if (this.downloadingPDF) return;
      
      this.downloadingPDF = true;
      
      try {
        console.log('Generating PDF for route:', route);
        
        const solverType = this.getSolverType(route);
        
        // Generate PDF based on solver type
        switch (solverType) {
          case 'VRP':
            await PDFTemplates.generateVRPReport(route, this.vehicles);
            break;
          case 'TSP':
            await PDFTemplates.generateTSPReport(route, this.vehicles);
            break;
          case 'LAP':
            await PDFTemplates.generateLAPReport(route);
            break;
          default:
            console.warn('Unknown solver type:', solverType);
            // Default to VRP template
            await PDFTemplates.generateVRPReport(route, this.vehicles);
        }
        
        // Show success message (optional)
        console.log('PDF generated successfully');
        
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('PDF oluşturulurken hata oluştu. Lütfen tekrar deneyin.');
      } finally {
        this.downloadingPDF = false;
      }
    },
    
    /**
     * Download Excel report for the route
     */
    async downloadExcel(route) {
      if (this.downloadingExcel) return;
      
      this.downloadingExcel = true;
      
      try {
        console.log('Generating Excel for route:', route);
        
        // Use the ExcelExportService to generate and download the Excel file
        ExcelExportService.exportRoute(route);
        
        // Show success message (optional)
        console.log('Excel generated successfully');
        
      } catch (error) {
        console.error('Error generating Excel:', error);
        alert('Excel oluşturulurken hata oluştu. Lütfen tekrar deneyin.');
      } finally {
        this.downloadingExcel = false;
      }
    },
    
    async updateStatus(route, newStatus) {
      if (route.status === newStatus) return;
      
      // Use _id first (MongoDB ObjectId), then fallback to id field
      const routeId = route._id || route.id;
      if (!routeId) {
        console.error('Cannot update: missing route id', route);
        return alert('Cannot update: missing route id');
      }
      
      console.log('Updating route status:', { routeId, newStatus, route });
      
      try {
        const response = await axios.patch(
          `${API_URL}/api/analytics/saved-routes/${routeId}/status`,
          { status: newStatus }
        );
        console.log('Status update response:', response.data);
        route.status = newStatus;
      } catch (e) {
        console.error('Failed to update status:', e.response?.data || e.message);
        alert(`Failed to update status: ${e.response?.data?.error || e.message}`);
      }
    },
    
    async deleteRoute(route) {
      if (this.deletingRoute) return;
      
      // Show confirmation dialog
      const routeName = route.name || 'Unnamed Route';
      const confirmMessage = `Bu rotayı silmek istediğinizden emin misiniz?\n\n"${routeName}"\n\nBu işlem geri alınamaz.`;
      
      if (!confirm(confirmMessage)) {
        return;
      }
      
      // Use _id first (MongoDB ObjectId), then fallback to id field
      const routeId = route._id || route.id;
      if (!routeId) {
        console.error('Cannot delete: missing route id', route);
        return alert('Silinemedi: route id eksik');
      }
      
      console.log('Deleting route:', { routeId, route });
      
      this.deletingRoute = true;
      
      try {
        const response = await axios.delete(`${API_URL}/api/analytics/saved-routes/${routeId}`);
        console.log('Delete response:', response.data);
        
        // Remove route from local array
        const routeIndex = this.routes.findIndex(r => (r._id || r.id) === routeId);
        if (routeIndex !== -1) {
          this.routes.splice(routeIndex, 1);
          
          // If we deleted the currently expanded route, close the expansion
          if (this.expandedIndex === routeIndex) {
            this.expandedIndex = null;
          } else if (this.expandedIndex > routeIndex) {
            // Adjust expanded index if we deleted a route before the expanded one
            this.expandedIndex--;
          }
        }
        
        // Show success message
        alert(`Rota başarıyla silindi: ${routeName}`);
        
      } catch (e) {
        console.error('Failed to delete route:', e.response?.data || e.message);
        alert(`Rota silinemedi: ${e.response?.data?.error || e.message}`);
      } finally {
        this.deletingRoute = false;
      }
    },
    
    /**
     * Open the map modal for a specific route
     */
    openMapModal(route) {
      this.selectedRouteForMap = route;
      this.showMapModal = true;
    },
    
    /**
     * Close the map modal
     */
    closeMapModal() {
      this.showMapModal = false;
      this.selectedRouteForMap = null;
    },
    
    /**
     * Open the route editor page for a specific route
     */
    openEditModal(route) {
      const routeId = route._id || route.id;
      this.$router.push(`/route-editor/${routeId}`);
    },
    
    /**
     * Close the edit modal
     */
    closeEditModal() {
      this.showEditModal = false;
      this.selectedRouteForEdit = null;
    },
    
    /**
     * Handle route updated event from edit modal
     */
    onRouteUpdated(updatedRoute) {
      // Find and update the route in the local array
      const routeIndex = this.routes.findIndex(r => (r._id || r.id) === (updatedRoute._id || updatedRoute.id));
      if (routeIndex !== -1) {
        this.routes.splice(routeIndex, 1, updatedRoute);
      }
    },
    
    /**
     * Check if a route can be edited
     */
    canEditRoute(route) {
      // Only allow editing VRP and TSP routes (not LAP)
      const solverType = this.getSolverType(route);
      if (solverType === 'LAP') {
        return false;
      }
      
      // Must have route data and locations
      if (!route.routes || !route.routes.length || !route.allLocations || !route.allLocations.length) {
        return false;
      }
      
      // Must have at least one vehicle with stops
      const hasValidRoutes = route.routes.some(vehicleRoute => 
        vehicleRoute.stops && vehicleRoute.stops.length > 1
      );
      
      return hasValidRoutes;
    },
    
    /**
     * Get tooltip text for edit button
     */
    getEditTooltip(route) {
      if (!this.canEditRoute(route)) {
        const solverType = this.getSolverType(route);
        if (solverType === 'LAP') {
          return 'Location Allocation Problems cannot be edited manually';
        }
        if (!route.routes || !route.routes.length) {
          return 'Route has no vehicle data to edit';
        }
        if (!route.allLocations || !route.allLocations.length) {
          return 'Route has no location data to edit';
        }
        return 'Route cannot be edited - insufficient data';
      }
      return 'Edit route stops and order';
    },
    
    /**
     * Toggle vehicle section expand/collapse
     */
    toggleVehicleExpand(routeIdx, vehicleIdx) {
      const key = `${routeIdx}_${vehicleIdx}`;
      if (!this.expandedVehicles[key]) {
        this.expandedVehicles[key] = true;
      } else {
        this.expandedVehicles[key] = !this.expandedVehicles[key];
      }
    },
    
    /**
     * Check if vehicle section is expanded
     */
    isVehicleExpanded(routeIdx, vehicleIdx) {
      const key = `${routeIdx}_${vehicleIdx}`;
      return this.expandedVehicles[key] === true;
    }
  },
  mounted() {
    this.fetchRoutes();
  }
}
</script>

<style scoped>
.history-page {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
}
.history-list {
  width: 80%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
}
.history-item {
  width: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  margin-bottom: 0.5rem;
  overflow: hidden;
  transition: box-shadow 0.2s;
  position: relative;
}
.history-item.expanded {
  box-shadow: 0 4px 24px rgba(0,0,0,0.13);
  background: #f8f9fa;
}
.history-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 1.5rem 2rem;
  min-height: 90px;
  transition: background 0.2s;
  position: relative;
}
.status-logo {
  margin-right: 1.5rem;
}
.route-info {
  flex: 1;
}
.route-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: #222;
}
.route-date {
  font-size: 0.95rem;
  color: #888;
  margin-top: 0.2rem;
}

.route-duration {
  font-size: 0.9rem;
  color: #3498db;
  margin-top: 0.2rem;
  font-weight: 500;
}

.route-metrics {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  min-width: 140px;
}

.route-distance {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 0.2rem;
}

.distance-value {
  font-size: 2.2rem;
  font-weight: bold;
  color: #222;
  line-height: 1;
}

.distance-unit {
  font-size: 1rem;
  color: #888;
}

.carbon-footprint {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: #27ae60;
  font-weight: 600;
  background: rgba(39, 174, 96, 0.1);
  padding: 0.3rem 0.6rem;
  border-radius: 16px;
  border: 1px solid rgba(39, 174, 96, 0.2);
}

.footprint-icon {
  width: 16px;
  height: 16px;
  opacity: 0.8;
  filter: brightness(0) saturate(100%) invert(42%) sepia(93%) saturate(1352%) hue-rotate(87deg) brightness(90%) contrast(80%);
}

.carbon-value {
  font-size: 0.85rem;
  white-space: nowrap;
}

.carbon-details {
  font-size: 0.8rem;
  color: #7f8c8d;
  font-weight: 500;
  margin-left: 0.3rem;
}

.environmental-info {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.tree-equivalent,
.car-equivalent {
  font-size: 0.9rem;
  color: #2c3e50;
  font-weight: 500;
  padding: 0.3rem 0.8rem;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.tree-equivalent {
  background: rgba(39, 174, 96, 0.1);
  border: 1px solid rgba(39, 174, 96, 0.2);
  color: #27ae60;
}

.car-equivalent {
  background: rgba(52, 152, 219, 0.1);
  border: 1px solid rgba(52, 152, 219, 0.2);
  color: #3498db;
}
.expand-arrow {
  margin-left: 1.5rem;
  transition: transform 0.3s;
  display: flex;
  align-items: center;
}
.expand-arrow svg {
  display: block;
}
.expand-arrow.open {
  transform: rotate(180deg);
}
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: max-height 0.4s cubic-bezier(.4,2,.6,1), opacity 0.3s;
  overflow: hidden;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
}
.slide-fade-enter-to,
.slide-fade-leave-from {
  max-height: 400px;
  opacity: 1;
}
.route-details {
  background: #fff;
  border-top: 1px solid #eee;
  padding: 1.5rem 2.5rem 1.5rem 6.5rem;
  font-size: 1.05rem;
  color: #333;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  animation: details-pop 0.4s;
}
@keyframes details-pop {
  0% { opacity: 0; transform: translateY(-10px);}
  100% { opacity: 1; transform: translateY(0);}
}
.detail-row {
  margin-bottom: 1rem;
  display: flex;
  gap: 1.5rem;
}
.detail-label {
  font-weight: 600;
  color: #3498db;
  min-width: 140px;
  display: inline-block;
}
.coords {
  color: #888;
  font-size: 0.95em;
  margin-left: 0.5em;
}
.visited-list {
  margin: 0;
  padding-left: 1.2em;
}
.done svg { filter: drop-shadow(0 0 6px #43e97b55); }
.rejected svg { filter: drop-shadow(0 0 6px #fa709a55); }
.planned svg { filter: drop-shadow(0 0 6px #ffd20055); }
.loading-message, .empty-message {
  text-align: center;
  color: #888;
  margin-top: 2rem;
}
.status-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: 1.5rem;
}
.status-btn {
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.1rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  opacity: 0.85;
  transition: box-shadow 0.2s, opacity 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
}
.status-btn.done { background: #43e97b; }
.status-btn.planned { background: #f7971e; }
.status-btn.rejected { background: #fa709a; }
.status-btn.active, .status-btn:hover { opacity: 1; box-shadow: 0 2px 8px rgba(0,0,0,0.13);}

/* Vehicle Details Styles */
.vehicles-section {
  margin-top: 1.5rem;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
}

.vehicle-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 4px solid #3498db;
}

.vehicle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 0.5rem;
  border-radius: 6px;
}

.vehicle-header:hover {
  background-color: rgba(52, 152, 219, 0.05);
}

.vehicle-title-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.vehicle-title {
  font-weight: 600;
  font-size: 1.05rem;
  color: #2c3e50;
}

.vehicle-id {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin-left: 0.5rem;
}

.vehicle-stats {
  display: flex;
  gap: 1rem;
}

.vehicle-stats .stat {
  background: white;
  padding: 0.3rem 0.8rem;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #2c3e50;
  border: 1px solid #dee2e6;
}

.vehicle-stats .stat.delivery-stat {
  background: #e8f5e8;
  border-color: #c3e6c3;
  color: #2d5a2d;
}

.vehicle-stats .stat.location-stat {
  background: #e8f1ff;
  border-color: #c3d9ff;
  color: #2d4a5a;
}

.vehicle-expand-arrow {
  margin-left: 1rem;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.vehicle-expand-arrow svg {
  display: block;
}

.vehicle-expand-arrow.open {
  transform: rotate(180deg);
}

/* Vehicle section slide transition */
.vehicle-slide-fade-enter-active,
.vehicle-slide-fade-leave-active {
  transition: max-height 0.4s cubic-bezier(.4,2,.6,1), opacity 0.3s;
  overflow: hidden;
}

.vehicle-slide-fade-enter-from,
.vehicle-slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
}

.vehicle-slide-fade-enter-to,
.vehicle-slide-fade-leave-from {
  max-height: 1000px;
  opacity: 1;
}

.reach-times {
  margin-top: 1rem;
}

.reach-times-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.8rem;
}

.timeline {
  position: relative;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  position: relative;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 20px;
  width: 2px;
  height: calc(100% + 0.5rem);
  background: #dee2e6;
  z-index: 1;
}

.timeline-marker {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3498db;
  margin-right: 1rem;
  margin-top: 4px;
  position: relative;
  z-index: 2;
  flex-shrink: 0;
}

.timeline-item.is-depot .timeline-marker {
  background: #e74c3c;
  width: 16px;
  height: 16px;
  margin-top: 2px;
}

.timeline-item.on-time .timeline-marker {
  background: #27ae60;
  box-shadow: 0 0 0 3px rgba(39, 174, 96, 0.2);
}

.timeline-item.late .timeline-marker {
  background: #e67e22;
  box-shadow: 0 0 0 3px rgba(230, 126, 34, 0.2);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 3px rgba(230, 126, 34, 0.2); }
  50% { box-shadow: 0 0 0 6px rgba(230, 126, 34, 0.1); }
  100% { box-shadow: 0 0 0 3px rgba(230, 126, 34, 0.2); }
}

.timeline-content {
  flex: 1;
}

.location-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.2rem;
}

.reach-time {
  font-weight: 600;
  color: #3498db;
  font-size: 0.95rem;
  margin-bottom: 0.2rem;
}

.demand-quantity {
  margin: 0.3rem 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.demand-quantity.is-depot {
  opacity: 0.8;
}

.demand-label {
  color: #7f8c8d;
  font-weight: 500;
  font-size: 0.85rem;
}

.demand-value {
  color: #2c3e50;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.85rem;
  min-width: 60px;
  text-align: center;
}

.demand-value.delivery-value {
  background: #e8f5e8;
  border: 1px solid #c3e6c3;
  color: #2d5a2d;
}

.demand-value.depot-value {
  background: #f0f0f0;
  border: 1px solid #d0d0d0;
  color: #666;
}

.delivery-icon,
.depot-icon {
  font-size: 1rem;
  opacity: 0.8;
}

.visit-indicator {
  margin: 0.3rem 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.visit-label {
  color: #7f8c8d;
  font-weight: 500;
  font-size: 0.85rem;
}

.visit-value {
  color: #2c3e50;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.85rem;
  background: #f0f8ff;
  border: 1px solid #d0e7ff;
  color: #2d4a5a;
}

.visit-icon {
  font-size: 1rem;
  opacity: 0.8;
}

.time-window {
  margin: 0.3rem 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.time-window-label {
  color: #7f8c8d;
  font-weight: 500;
}

.time-window-value {
  color: #2c3e50;
  font-weight: 600;
  background: #f8f9fa;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.85rem;
  border: 1px solid #dee2e6;
}

.on-time-indicator {
  color: #27ae60;
  font-weight: 600;
  font-size: 0.85rem;
  background: rgba(39, 174, 96, 0.1);
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  border: 1px solid rgba(39, 174, 96, 0.3);
}

.late-indicator {
  color: #e67e22;
  font-weight: 600;
  font-size: 0.85rem;
  background: rgba(230, 126, 34, 0.1);
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  border: 1px solid rgba(230, 126, 34, 0.3);
  animation: glow 1.5s ease-in-out infinite alternate;
}

@keyframes glow {
  from { background: rgba(230, 126, 34, 0.1); }
  to { background: rgba(230, 126, 34, 0.2); }
}

.location-coords {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Solver Type Styling */
.solver-type {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.8rem;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.solver-type.solver-vrp {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
}

.solver-type.solver-tsp {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border: 1px solid rgba(240, 147, 251, 0.3);
}

.solver-type.solver-lap {
  background: linear-gradient(135deg, #8a2be2 0%, #6a1b9a 100%);
  border: 1px solid rgba(138, 43, 226, 0.3);
}

.solver-type.solver-unknown {
  background: linear-gradient(135deg, #a8a8a8 0%, #7f7f7f 100%);
  border: 1px solid rgba(168, 168, 168, 0.3);
}

/* LAP Section Styling */
.lap-section {
  margin-top: 1.5rem;
}

.lap-metrics {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 4px solid #8a2be2;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(138, 43, 226, 0.1);
}

.metric-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.metric-label {
  font-weight: 500;
  color: #7f8c8d;
  font-size: 0.9rem;
}

.metric-value {
  font-weight: 600;
  color: #8a2be2;
  font-size: 1rem;
}

.facility-utilization {
  margin-top: 1rem;
}

.facility-utilization h5 {
  font-size: 0.95rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.8rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}

.utilization-row {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid rgba(138, 43, 226, 0.1);
}

.facility-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.facility-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.utilization-percentage {
  font-weight: 600;
  color: #8a2be2;
  font-size: 0.9rem;
}

.utilization-bar {
  height: 8px;
  background-color: rgba(138, 43, 226, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.utilization-fill {
  height: 100%;
  background: linear-gradient(90deg, #8a2be2 0%, #6a1b9a 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.utilization-details {
  font-size: 0.85rem;
  color: #7f8c8d;
  text-align: right;
}

/* Download Section */
.download-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.8rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
  justify-content: center;
}

.download-btn.pdf-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.download-btn.excel-btn {
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  box-shadow: 0 2px 8px rgba(39, 174, 96, 0.3);
}

.download-btn.delete-btn {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.3);
}

.download-btn.pdf-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

.download-btn.excel-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(39, 174, 96, 0.4);
}

.download-btn.map-btn {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
}

.download-btn.edit-btn {
  background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
  box-shadow: 0 2px 8px rgba(155, 89, 182, 0.3);
}

.download-btn.delete-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(231, 76, 60, 0.4);
}

.download-btn.map-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(52, 152, 219, 0.4);
}

.download-btn.edit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(155, 89, 182, 0.4);
}

.download-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.download-btn.loading {
  background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
}

.download-btn svg {
  flex-shrink: 0;
}

/* Loading spinner */
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive design for mobile devices */
@media (max-width: 768px) {
  .history-header {
    flex-wrap: wrap;
    padding: 1rem 1.5rem;
  }
  
  .route-info {
    flex: 1;
    min-width: 100%;
    margin-bottom: 1rem;
  }
  
  .route-metrics {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-width: 100%;
    gap: 1rem;
  }
  
  .carbon-footprint {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
  }
  
  .footprint-icon {
    width: 14px;
    height: 14px;
  }
  
  .carbon-value {
    font-size: 0.75rem;
  }
  
  .distance-value {
    font-size: 1.8rem;
  }
  
  .expand-arrow {
    position: absolute;
    top: 1rem;
    right: 1.5rem;
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .history-list {
    width: 95%;
  }
  
  .history-header {
    padding: 1rem;
  }
  
  .route-metrics {
    gap: 0.5rem;
  }
  
  .carbon-footprint {
    font-size: 0.75rem;
    padding: 0.2rem 0.4rem;
  }
  
  .distance-value {
    font-size: 1.6rem;
  }
  
  .carbon-details {
    display: none; /* Hide per-km details on very small screens */
  }
}
</style>